/**
 * NVR 원격등록 템플릿 내보내기.
 *
 * 구역(zone)마다 NVR 한 대분 파일을 하나씩 만들어 zip 으로 묶는다. 납품 NVR 마다 형식이 다르다.
 *   - 기존 NVR: osong_nvr.xls 형식 .xls (IP / CH NAME 만 구역의 CCTV 값, 나머지는 템플릿 고정값)
 *     → nvr_template_{구역번호}.xls, nvr_template.zip
 *   - 포커스에이아이 NVR: 녹화기 웹 [CSV 등록] 샘플(camera_registration_template.csv) 형식 .csv
 *     → focusai_nvr_{구역번호}.csv, focusai_nvr_template.zip
 *
 * 구역번호는 구역 이름의 첫 숫자(예: "1구역", "Zone-3"), 숫자가 없으면 등장 순서 번호.
 *
 * 구역의 CCTV 는 존 생성 시 저장되는 zone.data.properties.cctvIds 를
 * 우선 사용하고, 없으면(수동 편집·구버전 저장) 폴리곤 포함 판정으로
 * 계산한다 — zoneCctvRelations 의 정책과 동일.
 */

import { zoneNumberFromName } from '../../../map-editor-shared/lib/zoneName'
import type { dia } from '@joint/core'
import { writeBiff8Xls } from './biff8Xls'
import { isCctvElement, pointInPolygon, type XYPoint } from '../../editor/lib/zoneCctvRelations'
import { FOCUSAI_HEADER, focusAiCsvText } from './focusAiCsv'

// osong_nvr.xls 1행 헤더 그대로
export const NVR_HEADER = [
  'Fixed', 'CH NUM', 'IP', 'PORT', 'ID', 'PASSWORD', 'CH NAME',
  'RTSP USED', 'RTSP PORT', 'RTSP MAIN URL', 'RTSP SUB URL',
] as const

// IP / CH NAME 외에 템플릿과 동일하게 유지하는 고정값
const FIXED = 'IPC'
const PORT = 80
const ID = 'admin'
// 문자열 셀(t:'s')로 둔다 — 실제 NVR 에 등록되는 파일(nvr_template_central/*.xls)이
// PASSWORD 를 텍스트 "1234" 로 갖고 있고, 숫자 셀로 낸 파일은 등록되지 않았다.
const PASSWORD = '1234'

/** 납품 NVR 종류. */
export type NvrTemplateKind = 'legacy' | 'focusai'

export const NVR_TEMPLATE_KINDS: Record<NvrTemplateKind, { label: string; zipName: string }> = {
  legacy: { label: '기존 NVR (.xls)', zipName: 'nvr_template.zip' },
  focusai: { label: '포커스에이아이 NVR (.csv)', zipName: 'focusai_nvr_template.zip' },
}

export { FOCUSAI_HEADER }

// 샘플 파일의 고정값. ID/PASSWORD 는 샘플이 자리 표시(id_text)라 기존 템플릿과 같은 카메라 계정을 쓴다.
const FOCUSAI_PROTOCOL = 'onvif'
const FOCUSAI_ONVIF_ADDRESS = '/onvif/device_service'
const FOCUSAI_RTSP_PORT = 554
const FOCUSAI_BRAND = 'ONVIF'
const FOCUSAI_MODEL = 'FIUB-8520RAGP'

export interface NvrChannel {
  ip: string
  chName: string
}

export interface ZoneNvrSheet {
  /** 파일명에 쓰는 구역번호 */
  zoneNumber: string
  zoneName: string
  channels: NvrChannel[]
}

type CellData = {
  properties?: Record<string, unknown>
  text?: string
} | undefined

function cellName(element: dia.Element): string {
  const data = element.get('data') as CellData
  const name = data?.properties?.name ?? data?.text
  return typeof name === 'string' ? name : ''
}

function cellIp(element: dia.Element): string {
  const data = element.get('data') as CellData
  const ip = data?.properties?.ipAddress
  return typeof ip === 'string' ? ip : ''
}

function isZoneElement(element: dia.Element): boolean {
  const data = element.get('data') as CellData
  return data?.properties?.kind === 'zone'
}

/**
 * CCTV 이름 `P{n}_B{n}_{구역}_{순번}` 에서 구역번호. 규격 밖 이름은 null.
 * 이름의 구역번호가 소속의 정답이다 — 구역 원이 겹치면 폴리곤 포함/저장 목록으로는
 * 다른 구역 카메라가 섞여 들어와 NVR 1번 서버에 2구역 카메라가 실린다.
 */
export function cctvZoneNumberFromName(name: string): string | null {
  const m = name.match(/^P\d+_B\d+_(\d+)_\d+$/)
  return m ? m[1] : null
}

/** `P1_B1_2_10` 순번(마지막 숫자). 규격 밖이면 Infinity(맨 뒤). */
function cctvSeqFromName(name: string): number {
  const m = name.match(/_(\d+)$/)
  return m ? Number(m[1]) : Number.POSITIVE_INFINITY
}

/** 구역 이름에서 구역번호 추출 — 마지막 숫자 그룹(B1_3 → 3), 없으면 fallback. */
export { zoneNumberFromName }

/**
 * 존 셀의 절대 좌표 폴리곤.
 *
 * 존 생성 경로에 따라 data.points 의 좌표계가 다르다: 직접 그린 존은 절대
 * 그래프 좌표지만, PDF 매핑(CCTV & 구역 매핑)으로 만든 존은 points 가 원본
 * PDF 좌표이고 절대 좌표는 data.vertices 에 있다. 그래서 후보(points →
 * vertices)의 bbox 가 셀의 실제 bbox 와 겹치는지 검증해서 올바른 좌표계를
 * 고르고, 둘 다 아니면 bbox 사각형으로 후퇴한다.
 */
export function zoneAbsolutePolygon(zone: dia.Element): XYPoint[] {
  const bbox = zone.getBBox()
  const data = zone.get('data') as { points?: unknown; vertices?: unknown } | undefined

  const candidates: XYPoint[][] = []
  if (Array.isArray(data?.points) && data.points.length >= 3) {
    candidates.push((data.points as { x: number; y: number }[]).map(p => ({ x: Number(p.x), y: Number(p.y) })))
  }
  if (Array.isArray(data?.vertices) && data.vertices.length >= 3) {
    candidates.push((data.vertices as [number, number][]).map(v => ({ x: Number(v[0]), y: Number(v[1]) })))
  }

  for (const poly of candidates) {
    const xs = poly.map(p => p.x)
    const ys = poly.map(p => p.y)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)
    const intersects =
      maxX >= bbox.x && minX <= bbox.x + bbox.width &&
      maxY >= bbox.y && minY <= bbox.y + bbox.height
    if (intersects) return poly
  }

  return [
    { x: bbox.x, y: bbox.y },
    { x: bbox.x + bbox.width, y: bbox.y },
    { x: bbox.x + bbox.width, y: bbox.y + bbox.height },
    { x: bbox.x, y: bbox.y + bbox.height },
  ]
}

/**
 * 여러 층의 그래프에서 구역별 NVR 채널 목록을 수집한다.
 * CH NUM 은 구역 안 CCTV 등장 순서(1부터)로 매긴다.
 */
export function collectZoneNvrSheets(graphs: dia.Graph[]): ZoneNvrSheet[] {
  const sheets: ZoneNvrSheet[] = []
  const usedNumbers = new Set<string>()

  for (const graph of graphs) {
    const elements = graph.getElements()
    const cctvs = elements.filter(isCctvElement)
    const byId = new Map<string, dia.Element>(cctvs.map(c => [String(c.id), c]))

    for (const zone of elements.filter(isZoneElement)) {
      const zoneName = cellName(zone)
      const baseNumber = zoneNumberFromName(zoneName, sheets.length + 1)

      // 1) 존 생성 시 저장된 포함 CCTV id 목록
      const data = zone.get('data') as CellData
      const savedIds = data?.properties?.cctvIds
      let members: dia.Element[] = Array.isArray(savedIds)
        ? (savedIds as unknown[])
            .map(id => byId.get(String(id)))
            .filter((c): c is dia.Element => !!c)
        : []

      // 2) 목록이 없으면 폴리곤 포함 판정으로 계산
      if (members.length === 0) {
        const polygon = zoneAbsolutePolygon(zone)
        members = cctvs.filter(cctv => {
          const bbox = cctv.getBBox()
          return pointInPolygon({ x: bbox.center().x, y: bbox.center().y }, polygon)
        })
      }

      // 3) 이름에 구역번호가 있는 카메라는 이름이 정답: 이 구역 번호인 카메라는
      //    목록/원 밖이어도 전부 담고, 다른 구역 번호인 카메라는 뺀다.
      //    이름이 규격 밖인 카메라만 1)/2) 결과를 그대로 따른다.
      const namedHere = cctvs
        .filter(c => cctvZoneNumberFromName(cellName(c)) === baseNumber)
        .sort((a, b) => cctvSeqFromName(cellName(a)) - cctvSeqFromName(cellName(b)))
      const unnamedMembers = members.filter(c => cctvZoneNumberFromName(cellName(c)) === null)
      members = [...namedHere, ...unnamedMembers]

      // 구역번호 중복 시 뒤에 온 쪽에 _2, _3 … 을 붙여 파일명 충돌을 막는다
      let zoneNumber = baseNumber
      if (usedNumbers.has(zoneNumber)) {
        let suffix = 2
        while (usedNumbers.has(`${zoneNumber}_${suffix}`)) suffix++
        zoneNumber = `${zoneNumber}_${suffix}`
      }
      usedNumbers.add(zoneNumber)

      sheets.push({
        zoneNumber,
        zoneName,
        channels: members.map(cctv => ({ ip: cellIp(cctv), chName: cellName(cctv) })),
      })
    }
  }
  return sheets
}

/** 시트 하나를 osong_nvr.xls 형식의 AOA(행 배열)로 변환. */
export function buildNvrRows(sheet: ZoneNvrSheet): (string | number | null)[][] {
  return [
    [...NVR_HEADER],
    // RTSP 4열은 null → BLANK 셀. 등록되는 파일(nvr_common.write_xls)이 "" 를 BLANK 로 쓰는 것과 같다.
    ...sheet.channels.map((ch, i) => [
      FIXED, i + 1, ch.ip, PORT, ID, PASSWORD, ch.chName, null, null, null, null,
    ]),
  ]
}

/**
 * 시트 하나를 NVR 이 읽는 BIFF8 .xls 바이트로 변환.
 * SheetJS 의 xls 작성기는 셀 내용이 같아도 NVR 이 거부해(2026-09-02) xlwt 와 바이트 동일한 자체 작성기를 쓴다.
 */
export function buildNvrXlsBytes(sheet: ZoneNvrSheet): Uint8Array {
  return writeBiff8Xls(buildNvrRows(sheet))
}

/** CSV 칸 하나 — 쉼표·따옴표·줄바꿈이 있으면 따옴표로 감싼다. */
/**
 * 시트 하나를 포커스에이아이 NVR [CSV 등록] 파일로 변환. 샘플과 같이 BOM 없이 CRLF, 마지막 줄도 CRLF.
 * channel 은 기존 템플릿의 CH NUM 과 같이 구역 안 순서(1부터), title 은 CCTV 이름.
 */
export function buildFocusAiCsv(sheet: ZoneNvrSheet): string {
  const rows: (string | number)[][] = [
    [...FOCUSAI_HEADER],
    ...sheet.channels.map((ch, i) => [
      FOCUSAI_PROTOCOL, ch.ip, FOCUSAI_ONVIF_ADDRESS, FOCUSAI_RTSP_PORT, i + 1,
      FOCUSAI_BRAND, FOCUSAI_MODEL, ID, PASSWORD, ch.chName,
    ]),
  ]
  return focusAiCsvText(rows)
}

/** 구역별 파일을 kind 형식으로 만들어 zip Blob 으로 반환 (파일 이름은 NVR_TEMPLATE_KINDS 참고). */
export async function generateNvrTemplateZip(
  graphs: dia.Graph[],
  kind: NvrTemplateKind = 'legacy',
): Promise<{ blob: Blob; fileCount: number }> {
  const sheets = collectZoneNvrSheets(graphs)
  if (sheets.length === 0) {
    throw new Error('구역이 없습니다 — 구역을 먼저 생성하세요.')
  }

  const { createZip } = await import('./zip')

  const files = sheets.map(sheet =>
    kind === 'focusai'
      ? { name: `focusai_nvr_${sheet.zoneNumber}.csv`, data: new TextEncoder().encode(buildFocusAiCsv(sheet)) }
      : { name: `nvr_template_${sheet.zoneNumber}.xls`, data: buildNvrXlsBytes(sheet) },
  )

  return { blob: new Blob([createZip(files)], { type: 'application/zip' }), fileCount: files.length }
}

/** Blob 을 파일로 다운로드. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
